// @flow

import React, { useRef, useState, useEffect, useCallback, Fragment } from 'react';
import type { Node } from 'react';
import { v4 } from 'uuid';
import md5 from 'md5';
import { useParams, useHistory } from 'react-router-dom';
import get from 'lodash/get';
import pick from 'lodash/pick';
import find from 'lodash/find';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { animateScroll } from 'react-scroll';
import { useIntl, FormattedMessage } from 'react-intl';
import LazyLoad from 'react-lazyload';
import {
  Link,
  MediaQuery,
  StickyScrollParent,
  ScrollMarkerProvider,
  ScrollMarkerMap,
  ScrollMarkerAnchor,
} from '@riseart/common';
import { Row, Col } from '@riseart/antd-provider';
import { Query } from 'shared_services/apollo/Query';
import { useQuery } from 'shared_services/apollo/useQuery';
import { LayoutDefault } from 'shared_components/layouts/Default';
import {
  NOTFOUND_STATE_KEYS,
  notFoundUpdate,
} from 'shared_services/redux/actions/application/notFound';
import { localeSelector } from 'shared_services/redux/selectors/locale';
import { selectVisitorId } from 'shared_services/redux/selectors/visitor';
import { selectAuthShippingCountryCode } from 'shared_services/redux/selectors/auth';
import { selectStoreCode } from 'shared_services/redux/selectors/storeCode';
import { selectUnitSystem } from 'shared_services/redux/selectors/unitSystem';
import { selectNotFound } from 'shared_services/redux/selectors/notFound';
import { useUrlParams } from 'shared_data/providers/url/useUrlParams';
import { SSRConsumer } from 'shared_data/providers/ssr/Consumer';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import { FilterArtistLinks } from 'shared_components/common/filters/links/Artists';
import { FilterCategoriesLinks } from 'shared_components/common/filters/links/Categories';
import { CmsViewModule } from 'shared_hocs/cms/views/Module';
import { Avatar } from 'shared_components/common/avatar/Avatar';
import { generateSrcForCDN } from 'shared_components/common/artdirection/picture/Picture';
import { DataProviderPreloaderPage } from 'shared_components/data/providers/preloaders/Page';
import { MetaProvider } from 'shared_data/providers/meta/Meta';
import { META_SUBSCRIBER_NAME } from 'shared_services/riseart/meta/Meta';
import { LocationManager } from 'shared_services/riseart/url/Location';
import {
  StorageProvider,
  storageAppendUniqueItems,
} from 'shared_services/riseart/storage/Provider';
import { StorageConsumer } from 'shared_services/riseart/storage/Consumer';
import { AddCartItemButton } from 'shared_data/providers/queries/AddCartItem';
import READ_ART_QUERY from 'shared_data/queries/art/read.graphql';
import LIST_ART_SIMILAR_QUERY from 'shared_data/queries/art/listSimilar.graphql';
import LIST_PRODUCT_THOUGHTS_QUERY from 'shared_data/queries/art/listProductThoughts.graphql';
import FILTER_ART_QUERY from 'shared_data/queries/filter/art.graphql';
import FILTER_ARTIST_EDITORIALS_QUERY from 'shared_data/queries/artist/filterEditorials.graphql';
import FILTER_PRODUCT_EDITORIALS_QUERY from 'shared_data/queries/art/filterProductEditorials.graphql';
import { components as COMPONENTS_CONFIG, storage as STORAGE_CONFIG } from 'Config';
import {
  analytics as ANALYTICS_ENUM,
  artist as ENUM_ARTIST,
  art as ENUM_ART,
  filter as FILTER_ENUM,
  meta as META_ENUM,
  HTTP_STATUS,
} from 'Enum';
import { LocalizedConfig } from 'shared_services/riseart/utils/LocalizedConfig';
import { generateHrefLangParams } from 'shared_services/riseart/meta/utils';
import { NotFoundArt } from 'shared_components/common/notfound/Art';
import { FilterBreadcrumbs } from 'shared_components/common/filters/breadcrumbs/Breadcrumbs';
import { ArtSlider } from 'shared_components/arts/slider/Slider';
import { ArtSliderSection } from 'shared_components/arts/containers/sections/Slider';
import { ListArtQuery } from 'shared_data/providers/queries/ListArtQuery';
import { ArtReviews } from 'shared_components/arts/reviews/Reviews';
import { getUrgentMessage } from 'shared_components/arts/containers/messages/Urgent';
import { ArtGalleryActions } from 'shared_components/arts/gallery/Actions';
import {
  getMasterImage,
  prepareImages,
  prepareGalleryImages,
  renderViewInRoomPicture,
  normalizeRawArtData,
  recentArtworksMapper,
} from 'shared_services/riseart/utils/artDataUtils';
import { actionFactory } from 'shared_services/riseart/utils/Utils';
import { LDTYPES, generateLDJSON } from 'shared_models/seo/ldJSON';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { GOOGLE_TAGMANAGER_ACTIONS } from 'shared_data/providers/google/tagmanager/Actions';

import {
  Section,
  SectionContent,
  Wrapper,
  Overlay,
  OverlayHeader,
  OverlayBody,
} from '@riseart/layout';
import {
  ArtDetails as ArtDetailsSection,
  normalizeData,
  definePurchaseProps,
} from 'shared_components/arts/detail/Detail';
import {
  ArtBio,
  ArtBioCuratorThought,
  ArtBioCuratorContact,
  ArtGallery,
  ArtCarousel,
  ArtActionCheckout,
  ArtOverlayHeader,
} from '@riseart/art';
import { ArtOverlayCarousel } from 'shared_components/arts/overlay/Carousel';
import { raScreenSmMax, raScreenMd } from '@riseart/antd-provider/dist/website/variables.less';
import {
  artDetailsGalleryWrapperCls,
  artDetailsGalleryCls,
  artDetailsTabSectionsCls,
  artDetailsHeadingWithLinkCls,
  artDetailsSectionBreadcrumbsCls,
  artDetailsSectionNarrowCls,
  artDetailsSameArtistCls,
  artDetailsSimilarCls,
  artDetailsScrollMarkerWrapperCls,
  artDetailsScrollMarkerCls,
  artDetailsCommentNoAuthorCls,
} from 'shared_components/pages/art/Details.less';

const MAX_SLIDE_ITEMS = 15;
const { ART: FILTER_DOMAIN_ART } = FILTER_ENUM.domain;
const FALLBACK_ART_SQUARE = find(COMPONENTS_CONFIG.art.fallbackImages, {
  type: ENUM_ART.image.type.TYPE_MAIN_SQUARE,
});
const {
  editorials: { TYPE_ARTIST_REASON },
} = ENUM_ARTIST;
const PRELOADER_SUBSCRIBER_IDS = {
  readArt: v4(),
  artEditorial: v4(),
  productThoughts: v4(),
  artistArt: v4(),
  similarArtwork: v4(),
};

/**
 * ArtDetailsPage
 *
 * @params {Object} props
 * @returns {Node}
 */
export default function ArtDetailsPage({ staticContext }: Object): Node {
  // Refs
  const tabsRef = useRef<Object | null>(null);
  const reviewsRef = useRef<Object | null>(null);
  const similarArtworksRef = useRef<Object | null>(null);
  const lastLoadedArtId = useRef<Object | null>(null);

  // Redux states
  const intl = useIntl();
  const { id: artId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const currentLocale = useSelector(localeSelector, shallowEqual);
  const { translatedLocation: location } = useUrlParams();
  const visitorId = useSelector(selectVisitorId);
  const store = useSelector(selectStoreCode);
  const shippingCountry = useSelector(selectAuthShippingCountryCode);
  const { unitSystem } = useSelector(selectUnitSystem, shallowEqual) || {};
  const notFoundArtwork = useSelector(selectNotFound(NOTFOUND_STATE_KEYS.ART));

  // Component state
  const [selectedGalleryImage, setSelectedGalleryImage] = useState<number | null>(null);
  const [selectedFrame, setSelectedFrame] = useState<Object | null>(null);

  // Load art
  const { loading, data, error } = useQuery(READ_ART_QUERY, {
    skip: !store || !artId,
    fetchPolicy: 'cache-and-network',
    variables: {
      artId,
      store,
      ...(shippingCountry ? { shippingCountry } : null),
    },
    context: {
      customOptions: {
        errorSuppressFromResponse: true,
        errorHandler: ({ graphQLErrors = [] }) => {
          const found404Error = find(
            graphQLErrors,
            (err) => err && err.errorInfo && err.errorInfo.status === HTTP_STATUS.NOT_FOUND,
          );

          if (found404Error) {
            const artworkInfo = get(found404Error, 'errorInfo.additional.artwork');
            const artistInfo = get(found404Error, 'errorInfo.additional.artist');

            if (artworkInfo) {
              dispatch(
                notFoundUpdate(NOTFOUND_STATE_KEYS.ART, { ...artworkInfo, artist: artistInfo }),
              );

              return { shouldOverwriteDefaultHandler: true };
            }

            const redirectQueryString = `?${LocalizedConfig.get(
              'navigation.uri.query.params.message',
            )}=${HTTP_STATUS.NOT_FOUND}`;

            if (staticContext) staticContext.status = HTTP_STATUS.MOVED_PERMANENTLY;

            if (artistInfo && artistInfo.active) {
              // redirect to the artist page if artist is active
              // here we cannot use history.replace() because the artist url
              // is not part of the react routed pages
              const artistUrl = UrlAssembler.artistProfile(artistInfo.id, artistInfo.alias, {
                locale: currentLocale,
              });

              if (artistUrl) {
                LocationManager.redirect(`${artistUrl}${redirectQueryString}`, {
                  status: HTTP_STATUS.MOVED_PERMANENTLY,
                  target: '_self',
                });
              }
            } else {
              history.replace(
                UrlAssembler.artList({
                  search: redirectQueryString,
                  locale: currentLocale,
                }),
              );
            }

            return { shouldOverwriteDefaultHandler: true };
          }

          return { shouldOverwriteDefaultHandler: false };
        },
      },
    },
  });
  const artistArtRequest = useQuery(FILTER_ART_QUERY, {
    skip: !store || !artId,
    variables: {
      artistArtId: artId,
      inputArtFilter: { store, items: MAX_SLIDE_ITEMS },
      ignoreArtIds: artId,
    },
  });
  const artistArtItems = get(artistArtRequest, 'data.filterArt.items');
  const responseData = data && data.readArt;
  const loadedArtId = responseData && responseData.id;
  const isNewArtworkLoaded = loadedArtId && lastLoadedArtId.current !== loadedArtId;

  const scrollToSimilarArtworks = useCallback(() => {
    const { hash } = location;

    if (
      hash &&
      similarArtworksRef &&
      similarArtworksRef.current &&
      hash.replace('#', '') === 'similarArtworks'
    ) {
      animateScroll.scrollTo(similarArtworksRef.current.offsetTop);
    }
  }, [location]);

  // Run when component mounts
  useEffect(() => {
    scrollToSimilarArtworks();
  }, [scrollToSimilarArtworks]);

  // Dispatch new art view event if new artwork is loaded
  useEffect(() => {
    if (isNewArtworkLoaded) {
      dispatch(actionFactory(GOOGLE_TAGMANAGER_ACTIONS.ART_VIEW, { artData: responseData, store }));
    }
  }, [isNewArtworkLoaded, dispatch, responseData, store]);

  // Reset states if new art is loaded
  useEffect(() => {
    if (isNewArtworkLoaded) {
      if (lastLoadedArtId.current) {
        // Reset states after a new artwork is loaded only after the initial render
        setSelectedGalleryImage(null);
        setSelectedFrame(null);
      }

      // Update last loaded art id
      lastLoadedArtId.current = loadedArtId;
    }
  }, [isNewArtworkLoaded, loadedArtId]);

  /**
   * handleReviewLinkClick
   */
  const handleReviewLinkClick = () => {
    if (reviewsRef.current) {
      animateScroll.scrollTo(reviewsRef.current.offsetTop);
    }
  };

  /**
   * handleGalleryImageClick
   *
   * @param {number} idx
   */
  const handleGalleryImageClick = useCallback(
    (idx: number) => setSelectedGalleryImage(idx),
    [setSelectedGalleryImage],
  );

  /**
   * handleOverlayClose
   */
  const handleOverlayClose = useCallback(
    () => setSelectedGalleryImage(null),
    [setSelectedGalleryImage],
  );

  /**
   * handleFrameSelect
   *
   * @param {object} frame
   */
  const handleFrameSelect = useCallback(
    (frame: Object) => setSelectedFrame(frame),
    [setSelectedFrame],
  );

  // Prepare render data and components
  if (
    !responseData &&
    notFoundArtwork &&
    artId &&
    parseInt(notFoundArtwork.id, 10) === parseInt(artId, 10)
  ) {
    return (
      <LayoutDefault scrollTopOnPathChange>
        <NotFoundArt data={notFoundArtwork} metaSubscriber={META_SUBSCRIBER_NAME.ART_META} />
      </LayoutDefault>
    );
  }

  const hasError = !!error || !responseData;
  const artData = responseData
    ? normalizeRawArtData(responseData, store, currentLocale, unitSystem)
    : {};
  const canonicalURL = responseData
    ? UrlAssembler.artDetail(responseData.id, responseData.slug, {
        origin: LocationManager.get('origin'),
        locale: currentLocale,
      })
    : LocationManager.get('originWithPath');
  const hrefLangParams = responseData ? generateHrefLangParams(responseData.locales) : null;
  const metaData = artData ? { name: artData.title, artist: artData.artistName } : {};
  const artistArtUrl = artData
    ? UrlAssembler.artistSubpage('artistArt', artData.artistId, artData.artistAlias)
    : null;
  const getArtOverlayHeader = () => {
    const checkoutProps = definePurchaseProps(
      normalizeData(responseData, store, intl.formatMessage, currentLocale.name),
      selectedFrame,
      currentLocale.name,
    );

    return (
      <ArtOverlayHeader
        checkoutProps={checkoutProps}
        productCanBuy={responseData.productCanBuy}
        title={artData.title}
        artistUrl={artData.artistUrl}
        artistName={artData.artistName}
        selectedFrame={selectedFrame}
        storeCode={store}
        translations={{
          by: <FormattedMessage id="components.art.by" />,
        }}
      >
        <AddCartItemButton
          cartId="buy"
          qty={1}
          skuId={checkoutProps.productId}
          options={checkoutProps.options}
        >
          {({ loading, onClick }) => {
            return <ArtActionCheckout {...checkoutProps} onClick={onClick} loading={loading} />;
          }}
        </AddCartItemButton>
      </ArtOverlayHeader>
    );
  };
  const galleryImages = responseData ? prepareImages(artData, intl.formatMessage) : [];
  const galleryActionsProps = responseData
    ? {
        art: responseData,
        canonicalURL,
        artworkPreview:
          !responseData.is3D &&
          renderViewInRoomPicture({
            images: artData.images,
            width: artData.width,
            height: artData.height,
            alt: intl.formatMessage(
              { id: 'components.art.artwork_by_artist' },
              { title: artData.title, artist: artData.artistName },
            ),
          }),
        image: get(galleryImages, '[0].original.props.image.url'),
        url: LocationManager.get('href'),
        viewInRoomOverlayHeader: getArtOverlayHeader,
        selectedFrame,
        showVisuallySimilar: responseData.isProduct,
      }
    : {};
  const masterImage = artData && getMasterImage(artData);
  const sharedImage = masterImage ? generateSrcForCDN({ ...masterImage, width: 1200 }) : null;
  const artBioCuratorContactComponent = (
    <ArtBioCuratorContact
      options={{ headingTag: 'span' }}
      title={intl.formatMessage({ id: 'components.art.need_help.title' })}
      text={intl.formatMessage({ id: 'components.art.need_help.text' })}
      buttonText={intl.formatMessage({ id: 'components.art.need_help.button' })}
      buttonProps={{
        isAnchor: true,
        external: true,
        href: LocalizedConfig.get('externalUrls.talkToCurator'),
        target: '_blank',
      }}
    />
  );

  // Section titles
  const similarSectionTitle = intl.formatMessage({ id: 'components.art.similarArtworks' });

  // Section placeholders
  const SimilarArtworksPlaceholder = (
    <ArtSliderSection
      background="gray"
      className={artDetailsSimilarCls}
      title={similarSectionTitle}
      sectionContentProps={{
        headingProps: { tag: 'span' },
        headingSubTitleProps: { tag: 'span' },
      }}
    >
      <ArtSlider placeholder disableHeadingTags />
    </ArtSliderSection>
  );

  const TabsSection = (
    <Query
      key="art-tabs"
      query={artData.productId ? FILTER_PRODUCT_EDITORIALS_QUERY : FILTER_ARTIST_EDITORIALS_QUERY}
      variables={
        artData.productId
          ? {
              artistId: artData.artistId,
              productId: artData.productId,
              type: TYPE_ARTIST_REASON,
            }
          : {
              artistId: artData.artistId,
              type: TYPE_ARTIST_REASON,
              page: 1,
              items: 5,
            }
      }
      ssr={false}
    >
      {({ loading, data = {} }) => {
        const editorials: Object = {
          artist: [],
          additionalInfo: [],
          relatedArticles: [],
        };

        if (!loading) {
          editorials.artist = get(data, 'artistEditorials.items', []);
          editorials.additionalInfo = get(data, 'productEditorialsText.items', []);
          editorials.relatedArticles = [
            ...get(data, 'productArticles.items', []),
            ...get(data, 'productEditorialsLinks.items', []),
          ];
        }

        const hasEditorialData = !!find(editorials, (i) => i && i.length);
        const showLeftEditorialColumn =
          hasEditorialData &&
          ((editorials.artist && editorials.artist.length > 0) ||
            (editorials.additionalInfo && editorials.additionalInfo.length > 0));

        return (
          <div className={artDetailsTabSectionsCls}>
            <DataProviderPreloaderPage
              uniqueId={PRELOADER_SUBSCRIBER_IDS.artEditorial}
              attach={loading}
            />
            <CmsViewModule placeholder="cmsFaqs" skipFallback>
              {(cmsFaqs: Object) => (
                <CmsViewModule
                  placeholder="cmsTabs"
                  skipFallback
                  moduleProps={{
                    intl,
                    is3D: responseData.is3D,
                    artData,
                    hasEditorialData,
                    showLeftEditorialColumn,
                    editorials,
                    currentLocale,
                    activeTab:
                      artData && artData.showDescription && artData.description ? [0, 1] : [0],
                    cmsFaqs,
                  }}
                />
              )}
            </CmsViewModule>
          </div>
        );
      }}
    </Query>
  );

  return (
    <LayoutDefault scrollTopOnPathChange>
      <DataProviderPreloaderPage uniqueId={PRELOADER_SUBSCRIBER_IDS.readArt} attach={loading} />
      <DataProviderPreloaderPage
        uniqueId={PRELOADER_SUBSCRIBER_IDS.artistArt}
        attach={artistArtRequest.loading}
      />
      {loading ? (
        <IsomorphicRipple isActive isFullScreen />
      ) : (
        <Fragment>
          <MetaProvider
            metaSubscriber={META_SUBSCRIBER_NAME.ART_META}
            meta={
              responseData
                ? {
                    linkCanonical: canonicalURL,
                    linkCanonicalRedirect: UrlAssembler.artDetail(
                      responseData.id,
                      responseData.slug,
                      {
                        ...(location.search ? { search: location.search } : null),
                        ...(location.hash ? { hash: location.hash } : null),
                      },
                    ),
                    metaTitle: intl.formatMessage(
                      { id: 'meta.artDetails.dynamicMetaTitle' },
                      metaData,
                    ),
                    metaDescription: intl.formatMessage(
                      { id: 'meta.artDetails.dynamicMetaDescription' },
                      metaData,
                    ),
                    pageTitle: intl.formatMessage(
                      { id: 'meta.artDetails.dynamicPageTitle' },
                      metaData,
                    ),
                    ogTitle: intl.formatMessage(
                      { id: 'components.share.messages.artwork_detail.facebook' },
                      metaData,
                    ),
                    ogDescription: intl.formatMessage(
                      { id: 'meta.artDetails.dynamicMetaDescription' },
                      metaData,
                    ),
                    ogImage: sharedImage,
                    ogUrl: canonicalURL,
                    linkHreflang: hrefLangParams,
                    linkHreflangRedirect: hrefLangParams,
                    ...(data.meta || {}),
                  }
                : null
            }
            hasError={hasError}
          />
          <MetaProvider
            meta={
              responseData
                ? {
                    structuredDataArtwork: generateLDJSON(LDTYPES.ARTWORK, {
                      ...artData,
                      mediumKey: responseData.medium,
                      medium: intl.formatMessage({
                        id: `filters.art.medium.${responseData.medium}`,
                      }),
                      description: responseData.description,
                      url: canonicalURL,
                      artistUrl:
                        artData && artData.artistUrl
                          ? `${LocationManager.get('origin')}${artData.artistUrl}`
                          : null,
                      image: sharedImage,
                      locationCreated: artData.shipsFromCode
                        ? intl.formatMessage({
                            id: `countries.${artData.shipsFromCode}`,
                          })
                        : '',
                      mainEntityOfPage: { type: 'WebPage', url: canonicalURL },
                    }),
                    ...(artData &&
                    artData.isProduct &&
                    artData.productCanBuy &&
                    artData.productStore
                      ? {
                          structuredDataProduct: generateLDJSON(LDTYPES.PRODUCT, {
                            ...artData,
                            ...pick(responseData, [
                              'description',
                              'productCanBuy',
                              'productCanRent',
                              'productIsEnquire',
                              'productIsSoldOut',
                            ]),
                            image: sharedImage,
                            url: canonicalURL,
                            artistUrl:
                              artData && artData.artistUrl
                                ? `${LocationManager.get('origin')}${artData.artistUrl}`
                                : null,
                          }),
                        }
                      : {}),
                  }
                : null
            }
            hasError={hasError}
          />
          {responseData ? (
            <Fragment>
              <SSRConsumer>
                {({ isSSR }) =>
                  !isSSR ? (
                    <StorageProvider
                      storageType={STORAGE_CONFIG.recentlyViewedArtworks.type}
                      storageKey={STORAGE_CONFIG.recentlyViewedArtworks.key}
                      limit={STORAGE_CONFIG.recentlyViewedArtworks.limit}
                      value={{
                        id: artData.id,
                        medium: artData.medium,
                        width: artData.width,
                        height: artData.height,
                        slug: responseData.slug,
                        productType: responseData.productType,
                        storeCode: get(artData, 'productStore.storeCode'),
                        title: artData.title,
                        canBuy: artData.productCanBuy,
                        artistName: artData.artistName,
                        artistUrl: artData.artistUrl,
                        artistId: artData.artistId,
                        artistAlias: artData.artistAlias,
                        unit: responseData.units,
                        images: responseData.images,
                        dateAddedInStorage: Date.now(),
                      }}
                      dataMapper={storageAppendUniqueItems}
                    />
                  ) : null
                }
              </SSRConsumer>
              <Section className={artDetailsSectionBreadcrumbsCls} padding="none">
                <Wrapper>
                  <FilterBreadcrumbs
                    includePageRootLink={false}
                    includeMediumInStyle
                    filterDomain={FILTER_DOMAIN_ART}
                    filters={{
                      medium: responseData.medium ? [responseData.medium] : null,
                      style: responseData.style ? [responseData.style] : null,
                    }}
                    config={['medium', 'style']}
                    additionalLinks={[
                      { name: artData.artistName, url: artData.artistUrl },
                      {
                        isLink: false,
                        name: artData.title,
                        url: LocationManager.get('pathname'),
                      },
                    ]}
                  />
                </Wrapper>
              </Section>
              {/* Artwork Action Section: Art Gallery, Art Details & Actions Section */}
              <Section>
                <Wrapper
                  sizes={{
                    xs: 'wide',
                    sm: 'wide',
                    md: 'wide',
                    lg: 'narrow',
                    xl: 'narrow',
                  }}
                >
                  {selectedGalleryImage !== null && (
                    <Overlay isVisible onClose={handleOverlayClose}>
                      <OverlayHeader>{getArtOverlayHeader()}</OverlayHeader>
                      <OverlayBody>
                        <ArtOverlayCarousel
                          masterImage={masterImage}
                          images={prepareGalleryImages(artData, intl.formatMessage)}
                          media={responseData ? responseData.media : null}
                          initialSlide={selectedGalleryImage || 0}
                        />
                      </OverlayBody>
                    </Overlay>
                  )}
                  <Row type="flex" justify="space-between">
                    <ScrollMarkerProvider
                      onItemClick={(item, subscribers) => {
                        // scrolls to bottom edge of browser based on positioned anchors
                        const OFFSET_PADDING = 50;
                        const lastItem = subscribers[subscribers.length - 1];
                        const lastItemPositionFix =
                          lastItem && lastItem.id !== item.id
                            ? window.innerHeight - OFFSET_PADDING
                            : OFFSET_PADDING;
                        const scrollPosition =
                          window.pageYOffset +
                          item.anchorRef.getBoundingClientRect().top -
                          lastItemPositionFix;
                        animateScroll.scrollTo(scrollPosition, { duration: 500 });
                      }}
                    >
                      <Col span={0} lg={1} className={artDetailsScrollMarkerWrapperCls}>
                        {galleryImages.length > 2 ? (
                          <StickyScrollParent>
                            <ScrollMarkerMap className={artDetailsScrollMarkerCls} />
                          </StickyScrollParent>
                        ) : null}
                      </Col>
                      <Col span={24} md={13} lg={14} className={artDetailsGalleryWrapperCls}>
                        <MediaQuery minWidth={0}>
                          <MediaQuery maxWidth={raScreenSmMax}>
                            <ArtCarousel
                              className={artDetailsGalleryCls}
                              images={galleryImages}
                              media={responseData ? responseData.media : null}
                              actions={() => <ArtGalleryActions {...galleryActionsProps} />}
                              onClickItem={handleGalleryImageClick}
                            />
                          </MediaQuery>
                          <MediaQuery minWidth={raScreenMd}>
                            <ArtGallery
                              className={artDetailsGalleryCls}
                              imgRenderer={(image, key, columnIndex) => (
                                <Fragment>
                                  {columnIndex === 0 ? (
                                    <ScrollMarkerAnchor id={`img-grid-item-${key}`} />
                                  ) : null}
                                  {image}
                                </Fragment>
                              )}
                              images={galleryImages.map((i) => i && i.original)}
                              media={responseData ? responseData.media : null}
                              actions={() => <ArtGalleryActions {...galleryActionsProps} />}
                              onClickItem={handleGalleryImageClick}
                            />
                            {/* Tabs (Tablet & Desktop) */}
                            <span ref={tabsRef} />
                            {TabsSection}
                          </MediaQuery>
                        </MediaQuery>
                      </Col>
                    </ScrollMarkerProvider>
                    <Col span={24} md={10} lg={8}>
                      <ArtDetailsSection
                        intl={intl}
                        data={{
                          ...responseData,
                          artistName: artData.artistName,
                          dimensions: artData.artworkShortDimensionsLabel,
                          sharedImage,
                        }}
                        storeCode={store}
                        localeCode={currentLocale.name}
                        shippingCountryCode={shippingCountry}
                        onFrameSelect={handleFrameSelect}
                        handleReviewLinkClick={handleReviewLinkClick}
                        urgencyData={getUrgentMessage(responseData, intl.formatMessage)}
                      />
                    </Col>
                  </Row>
                </Wrapper>
              </Section>
              {/* Tabs (Mobile) */}
              <MediaQuery maxWidth={raScreenSmMax}>
                <Section>
                  <Wrapper
                    sizes={{
                      xs: 'wide',
                      sm: 'wide',
                      md: 'wide',
                      lg: 'narrow',
                      xl: 'narrow',
                    }}
                  >
                    <span ref={tabsRef} />
                    {TabsSection}
                  </Wrapper>
                </Section>
              </MediaQuery>
              {/* More Artwork by Artist */}
              {artistArtItems && artistArtItems.length ? (
                <Fragment>
                  <MetaProvider
                    meta={{
                      [META_ENUM.METATYPE.SCRIPT_LD_JSON_ARTWORKS_ARTIST]: generateLDJSON(
                        LDTYPES.CAROUSEL_ART,
                        artistArtItems.map((i) => {
                          return {
                            ...i,
                            url: UrlAssembler.artDetail(i.id, i.slug, {
                              origin: LocationManager.get('origin'),
                              locale: currentLocale,
                            }),
                            artistUrl: i.artistId
                              ? UrlAssembler.artistProfile(i.artistId, i.artistAlias, {
                                  origin: LocationManager.get('origin'),
                                })
                              : null,
                            image: generateSrcForCDN({
                              ...(find(i.images, {
                                type: ENUM_ART.image.type.TYPE_FLAT_SQUARE,
                              }) || FALLBACK_ART_SQUARE),
                              width: 1200,
                            }),
                          };
                        }),
                      ),
                    }}
                  />
                  <Section background="gray" className={artDetailsSameArtistCls}>
                    <Wrapper
                      sizes={{
                        xs: 'wide',
                        sm: 'wide',
                        md: 'wide',
                        lg: 'narrow',
                        xl: 'narrow',
                      }}
                    >
                      <SectionContent
                        nodeType="section"
                        title={
                          <FormattedMessage
                            id="components.art.more_artwork_by"
                            values={{ name: artData.artistName }}
                          >
                            {(moreArtworkByTitle: string) =>
                              artistArtUrl ? (
                                <Link to={artistArtUrl} title={moreArtworkByTitle}>
                                  {moreArtworkByTitle}
                                </Link>
                              ) : (
                                moreArtworkByTitle
                              )
                            }
                          </FormattedMessage>
                        }
                        headingProps={{
                          className: artDetailsHeadingWithLinkCls,
                          tag: 'span',
                        }}
                        link={
                          artistArtUrl ? (
                            <FormattedMessage
                              id="components.art.view_all_by"
                              values={{ name: artData.artistName }}
                            >
                              {(translatedText: string) => (
                                <Link to={artistArtUrl} title={translatedText}>
                                  {translatedText}
                                </Link>
                              )}
                            </FormattedMessage>
                          ) : null
                        }
                      >
                        <ArtSlider
                          disableHeadingTags
                          columnCount="4"
                          items={artistArtItems}
                          artCardProps={{ showAuthor: false }}
                          tracking={{
                            enabled: true,
                            enableInitial: false,
                            enableVisibility: true,
                            enableClick: true,
                            name: ANALYTICS_ENUM.list.name.ARTWORK_ARTIST_ART,
                          }}
                        />
                      </SectionContent>
                    </Wrapper>
                  </Section>
                </Fragment>
              ) : null}
              {/* Artist Bio, Currator's thoughts */}
              <Section>
                <Wrapper
                  sizes={{
                    xs: 'wide',
                    sm: 'narrow',
                    md: 'narrow',
                    lg: 'narrow',
                    xl: 'narrow',
                  }}
                >
                  <SectionContent className={artDetailsSectionNarrowCls}>
                    <Row type="flex" justify="space-between">
                      {/* Artist Bio */}
                      <Col span={24} lg={13}>
                        <ArtBio
                          options={{ headingTag: 'span' }}
                          name={
                            artData.artistUrl ? (
                              <Link href={artData.artistUrl} title={artData.artistName}>
                                {artData.artistName}
                              </Link>
                            ) : (
                              artData.artistName
                            )
                          }
                          profileLink={
                            artData.artistUrl ? (
                              <FormattedMessage id="components.art.viewProfile">
                                {(label: string) => (
                                  <Link href={artData.artistUrl} title={label}>
                                    {label}
                                  </Link>
                                )}
                              </FormattedMessage>
                            ) : null
                          }
                          biography={artData.artistStatement}
                          translations={{
                            showMore: intl.formatMessage({
                              id: 'common.show_more',
                            }),
                            showLess: intl.formatMessage({
                              id: 'common.show_less',
                            }),
                          }}
                          avatar={
                            artData.artistUserAvatars ? (
                              <Avatar
                                images={artData.artistUserAvatars}
                                name={artData.artistName}
                              />
                            ) : null
                          }
                        />
                      </Col>
                      {/* Currator's thoughts */}
                      <Col span={24} lg={8}>
                        {artData.productId && (
                          <Query
                            query={LIST_PRODUCT_THOUGHTS_QUERY}
                            variables={{
                              productId: artData.productId,
                              page: 0,
                              items: 1,
                            }}
                            ssr={false}
                          >
                            {({ loading, error, data = {} }) => {
                              if (loading || error) {
                                return null;
                              }

                              const productThoughts = get(data, 'listProductThoughts.items[0]', {});

                              if (!productThoughts || !productThoughts.text) {
                                return artBioCuratorContactComponent;
                              }

                              const fullName = get(productThoughts, 'author.fullName');
                              const authorUrl = get(productThoughts, 'author.url');

                              return (
                                <Fragment>
                                  <DataProviderPreloaderPage
                                    uniqueId={PRELOADER_SUBSCRIBER_IDS.artEditorial}
                                    attach={loading}
                                  />
                                  <ArtBioCuratorThought
                                    text={productThoughts.text}
                                    avatar={
                                      productThoughts.author ? (
                                        <Avatar
                                          images={get(productThoughts, 'author.avatars')}
                                          name={fullName}
                                        >
                                          <FormattedMessage
                                            id="components.art.curatorAvatarHeading"
                                            values={{
                                              name: authorUrl ? (
                                                <Link
                                                  external
                                                  type="strong"
                                                  href={authorUrl}
                                                  title={fullName}
                                                >
                                                  <strong>{fullName}</strong>
                                                </Link>
                                              ) : (
                                                <strong>{fullName}</strong>
                                              ),
                                            }}
                                          />
                                        </Avatar>
                                      ) : (
                                        <div className={artDetailsCommentNoAuthorCls}>
                                          <FormattedMessage
                                            id="components.art.curatorHeading"
                                            tagName="strong"
                                          />
                                        </div>
                                      )
                                    }
                                  />
                                </Fragment>
                              );
                            }}
                          </Query>
                        )}
                        {(!artData.productId && artBioCuratorContactComponent) || null}
                      </Col>
                    </Row>
                  </SectionContent>
                </Wrapper>
              </Section>
              {/* Similar Artworks */}
              <span ref={similarArtworksRef} />
              <LazyLoad once offset={500} placeholder={SimilarArtworksPlaceholder}>
                <ListArtQuery
                  query={LIST_ART_SIMILAR_QUERY}
                  variables={{
                    artId,
                    store,
                    items: MAX_SLIDE_ITEMS,
                    analytics: {
                      visitorId,
                      pageType: ANALYTICS_ENUM.page.type.TYPE_PDP,
                      ...(visitorId ? { anonymousId: md5(visitorId) } : null),
                    },
                    ignoreArtistIds: artData.artistId,
                  }}
                  dataHandler={(data) => data.listArtSimilar}
                  preloadSubscriberId={PRELOADER_SUBSCRIBER_IDS.similarArtwork}
                  placeholder={SimilarArtworksPlaceholder}
                >
                  {(data) =>
                    data &&
                    data.length > 0 && (
                      <ArtSliderSection
                        background="gray"
                        className={artDetailsSimilarCls}
                        title={similarSectionTitle}
                        sectionContentProps={{
                          headingProps: { tag: 'span' },
                          headingSubTitleProps: { tag: 'span' },
                        }}
                      >
                        <MetaProvider
                          meta={{
                            [META_ENUM.METATYPE.SCRIPT_LD_JSON_ARTWORKS_SIMILAR]: generateLDJSON(
                              LDTYPES.CAROUSEL_ART,
                              data.map((i) => ({
                                ...i,
                                url: UrlAssembler.artDetail(i.id, i.slug, {
                                  origin: LocationManager.get('origin'),
                                  locale: currentLocale,
                                }),
                                artistUrl: i.artistId
                                  ? UrlAssembler.artistProfile(i.artistId, i.artistAlias, {
                                      origin: LocationManager.get('origin'),
                                    })
                                  : null,
                                image: generateSrcForCDN({
                                  ...(find(i.images, {
                                    type: ENUM_ART.image.type.TYPE_MAIN_SQUARE,
                                  }) || FALLBACK_ART_SQUARE),
                                  width: 1200,
                                }),
                              })),
                            ),
                          }}
                        />
                        <ArtSlider
                          disableHeadingTags
                          items={data}
                          tracking={{
                            enabled: true,
                            enableInitial: false,
                            enableVisibility: true,
                            enableClick: true,
                            name: ANALYTICS_ENUM.list.name.ARTWORK_SMILIAR_ART,
                          }}
                        />
                      </ArtSliderSection>
                    )
                  }
                </ListArtQuery>
              </LazyLoad>
              {/* Recently viewed items */}
              <SSRConsumer>
                {({ isSSR }) =>
                  !isSSR ? (
                    <StorageConsumer
                      storageType={STORAGE_CONFIG.recentlyViewedArtworks.type}
                      storageKey={STORAGE_CONFIG.recentlyViewedArtworks.key}
                      dataMapper={recentArtworksMapper(
                        STORAGE_CONFIG.recentlyViewedArtworks.timeLimitDays,
                        [responseData.id],
                      )}
                    >
                      {(recentlyViewedArtworks = []) =>
                        recentlyViewedArtworks && recentlyViewedArtworks.length ? (
                          <Section>
                            <Wrapper
                              sizes={{
                                xs: 'wide',
                                sm: 'wide',
                                md: 'wide',
                                lg: 'narrow',
                                xl: 'narrow',
                              }}
                            >
                              <SectionContent
                                title={intl.formatMessage({
                                  id: 'components.art.recentlyViewedArtworks',
                                })}
                                headingProps={{ tag: 'span' }}
                                headingSubTitleProps={{ tag: 'span' }}
                              >
                                <ArtSlider
                                  items={recentlyViewedArtworks}
                                  columnCount="7"
                                  tracking={{
                                    enabled: true,
                                    enableInitial: false,
                                    enableVisibility: true,
                                    enableClick: true,
                                    name: ANALYTICS_ENUM.list.name.ARTWORK_RECENT_ART,
                                  }}
                                />
                              </SectionContent>
                            </Wrapper>
                          </Section>
                        ) : null
                      }
                    </StorageConsumer>
                  ) : null
                }
              </SSRConsumer>

              {/* Artwork Reviews */}
              <span ref={reviewsRef} />
              <Section>
                <Wrapper
                  sizes={{
                    xs: 'wide',
                    sm: 'wide',
                    md: 'wide',
                    lg: 'narrow',
                    xl: 'narrow',
                  }}
                >
                  <SectionContent>
                    <ArtReviews />
                  </SectionContent>
                </Wrapper>
              </Section>
              <Section>
                <Wrapper
                  sizes={{
                    xs: 'wide',
                    sm: 'wide',
                    md: 'wide',
                    lg: 'narrow',
                    xl: 'narrow',
                  }}
                >
                  <SectionContent
                    title={intl.formatMessage({ id: 'components.art.similarArtistLinks' })}
                    headingProps={{ tag: 'span' }}
                    headingSubTitleProps={{ tag: 'span' }}
                  >
                    <FilterArtistLinks
                      domain={FILTER_ENUM.domain.ART}
                      medium={artData.medium}
                      style={artData.style}
                      ignoreArtistIds={artData.artistId}
                    />
                  </SectionContent>
                </Wrapper>
              </Section>
              <Section>
                <Wrapper
                  sizes={{
                    xs: 'wide',
                    sm: 'wide',
                    md: 'wide',
                    lg: 'narrow',
                    xl: 'narrow',
                  }}
                >
                  <SectionContent
                    title={intl.formatMessage({ id: 'components.art.similarCategoryLinks' })}
                    headingProps={{ tag: 'span' }}
                    headingSubTitleProps={{ tag: 'span' }}
                  >
                    <FilterCategoriesLinks
                      domain={FILTER_ENUM.domain.ART}
                      medium={artData.medium}
                      style={artData.style}
                    />
                  </SectionContent>
                </Wrapper>
              </Section>
            </Fragment>
          ) : null}
        </Fragment>
      )}
    </LayoutDefault>
  );
}
